import { createServerFn } from "@tanstack/react-start";
import { getRequestHeader, getRequestIP } from "@tanstack/react-start/server";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

/**
 * Called from the client right after a successful sign-in.
 * Server-side enforces the strict 3-device hard-lock.
 */
export const registerCurrentDevice = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator(
    (input: {
      deviceId: string;
      deviceName?: string | null;
      userAgent?: string | null;
      location?: string | null;
    }) => {
      if (!input?.deviceId || typeof input.deviceId !== "string" || input.deviceId.length < 8) {
        throw new Error("INVALID_DEVICE_ID");
      }
      return {
        deviceId: input.deviceId,
        deviceName: input.deviceName ?? null,
        userAgent: input.userAgent ?? null,
        location: input.location ?? null,
      };
    },
  )
  .handler(async ({ data, context }) => {


    // Derive real client IP from edge headers (Cloudflare / standard proxies).
    let ip: string | null = null;
    try {
      ip =
        getRequestHeader("cf-connecting-ip") ||
        (getRequestHeader("x-forwarded-for") || "").split(",")[0].trim() ||
        getRequestHeader("x-real-ip") ||
        getRequestIP({ xForwardedFor: true }) ||
        null;
    } catch {
      ip = null;
    }

    const { data: res, error } = await context.supabase.rpc("register_or_check_device", {
      _device_id: data.deviceId,
      _device_name: data.deviceName ?? undefined,
      _ip: ip ?? undefined,
      _user_agent: data.userAgent ?? undefined,
      _location: data.location ?? undefined,
    });
    if (error) throw new Error(error.message);
    return res as {
      status: "allowed" | "banned" | "device_limit_reached";
      active_count?: number;
      max_devices?: number;
      locked?: boolean;
    };
  });

export const getMyDevicesFn = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase.rpc("get_my_devices");
    if (error) throw new Error(error.message);
    return { devices: (data ?? []) as Array<{
      id: string;
      device_id: string;
      device_name: string | null;
      ip_address: string | null;
      location: string | null;
      user_agent: string | null;
      first_seen_at: string;
      last_seen_at: string;
      is_active: boolean;
    }> };
  });

export const getMyAccountFlagsFn = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { data, error } = await context.supabase.rpc("get_my_account_flags");
    if (error) throw new Error(error.message);
    return (data ?? {}) as {
      is_banned: boolean;
      ban_reason: string | null;
      account_status: "active" | "suspended" | "banned";
      session_epoch: number;
      max_devices: number;
    };
  });
